import React from 'react';
import { Modal, Skeleton, Spin } from 'antd';
import {
    SquaresFourIcon,
    SparkleIcon,
    ArrowsClockwiseIcon,
    GaugeIcon,
    SunIcon,
    PercentIcon,
    TreeEvergreenIcon,
    CubeIcon,
    CheckSquareIcon,
    CurrencyDollarIcon,
} from '@phosphor-icons/react';
import { useViewerStore } from '../../../../../store/store';
import type { SelectedDesignForPricing } from '../../../../../store/oneButtonStore';
import {
    addModulesToAllRoofsMaxFill,
    addModulesToRoofsByIds,
    removeGreyModulesAndSingleModuleRoofs,
    SetModuleGraph,
} from '../../Three-JS-Viewer/Controllers/ModuleControl';
import { categoryLayoutVariantDefs, filterRoofIdsBySide } from '../../../../../services/layoutVariantPrompts';
import { isLayoutProductionOverAnnualUsage, resolveLayoutRoofCount, resolveSiteTreeCount } from '../utils/layout';
import {
    resolveCatalogCardSnapshot,
    resolveDefaultFallbackSnapshotUrl,
    catalogCardSnapshotForDisplay,
} from '../utils/catalogCardSnapshot';
import type { CategoryLayoutCandidate } from '../utils/categoryBestVariantScoring';
import type { AiDesignsDataSource } from './DashboardAiDesignsSourceBadge';

export type AllAiCombinationsLayoutCardDetails = {
    estProd: number | undefined;
    estModules: number | undefined;
    systemKw: string;
    roofCount: number;
    categoryId: string;
    designId: string;
};

export type DashboardAllAiDesignCombinationsModalProps = {
    theme: 'dark' | 'light';
    open: boolean;
    onClose: () => void;
    roofCombinationsFromAi: any[];
    roofCards: any[];
    defaultMaxfillRoofIds: string[] | undefined;
    layoutProductionByKey: Record<string, number | undefined> | undefined;
    layoutModulesByKey: Record<string, number | undefined> | undefined;
    layoutMetricsByKey: Record<string, unknown> | undefined;
    layoutSnapshotsByKey: Record<string, string | undefined> | undefined;
    simulatingLayouts: boolean;
    activeModule: unknown;
    dashboardSystemSizeKw: number | null | undefined;
    annualUsageCapKwh: number | null | undefined;
    treeCount: number;
    selectedDesignForPricing: SelectedDesignForPricing | null;
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    pricingSelectionLoading: boolean;
    isLoadingSnapshots: boolean;
    getProposalIdForCard: (cardKey: string) => string;
    handleApplyLayoutClick: (layoutKey: string) => void | Promise<void>;
    handleSelectLayoutCard: (key: string, details: AllAiCombinationsLayoutCardDetails) => void;
    handlePricingDollarClick: (key: string, details: AllAiCombinationsLayoutCardDetails) => void;
    onPricingCallClick?: (key: string) => void;
    setSelectedRoofCombinationKey: (key: string) => void;
    treeScenariosByLayoutKey: Record<string, unknown[] | undefined> | undefined;
    setTreeCuttingForLayoutKey: (key: string) => void;
    setTreeCuttingPopupOpen: (open: boolean) => void;
    setAiPanelTab: (tab: 'ai' | 'saved' | 'tree' | 'wishlist') => void;
    setTreeCuttingConfirmForKey: (key: string | null) => void;
    setTreeCuttingConfirmOpen: (open: boolean) => void;
    sceneViewer: any;
    aiCategoryLayoutVariants: Record<string, unknown[]> | undefined;
    selectedRoofCombinationKey: string | null | undefined;
    bestRoofsForLayout: { roofFaceIds?: string[] };
    layoutCandidatesByCategory: Record<string, unknown> | undefined;
    setAltLayoutsCategory: (label: string) => void;
    setAltLayoutsLoading: (loading: boolean) => void;
    setAltLayoutsModalOpen: (open: boolean) => void;
};

export const DashboardAllAiDesignCombinationsModal: React.FC<DashboardAllAiDesignCombinationsModalProps> = ({
    theme,
    open,
    onClose,
    roofCombinationsFromAi,
    roofCards,
    defaultMaxfillRoofIds,
    layoutProductionByKey,
    layoutModulesByKey,
    layoutMetricsByKey,
    layoutSnapshotsByKey,
    simulatingLayouts,
    activeModule,
    dashboardSystemSizeKw,
    annualUsageCapKwh,
    treeCount,
    selectedDesignForPricing,
    pricingDesignForTrigger,
    pricingSelectionLoading,
    isLoadingSnapshots,
    getProposalIdForCard,
    handleApplyLayoutClick,
    handleSelectLayoutCard,
    handlePricingDollarClick,
    onPricingCallClick,
    setSelectedRoofCombinationKey,
    treeScenariosByLayoutKey,
    setTreeCuttingForLayoutKey,
    setTreeCuttingPopupOpen,
    setAiPanelTab,
    setTreeCuttingConfirmForKey,
    setTreeCuttingConfirmOpen,
    sceneViewer,
    aiCategoryLayoutVariants,
    selectedRoofCombinationKey,
    bestRoofsForLayout,
    layoutCandidatesByCategory,
    setAltLayoutsCategory,
    setAltLayoutsLoading,
    setAltLayoutsModalOpen,
}) => {
    const [snapshotPreview, setSnapshotPreview] = React.useState<{ url: string; title: string } | null>(null);
    const categoryMainLayoutByCategory = useViewerStore(
        (s: any) => (s.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>,
    );
    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const aiLayoutVariantSnapshots = useViewerStore(
        (s: any) => (s.aiLayoutVariantSnapshots ?? {}) as Record<string, string>,
    );
    const layoutRoofSummariesByKey = useViewerStore(
        (s: any) => (s.layoutRoofSummariesByKey ?? {}) as Record<string, unknown[]>,
    );
    const fallbackSnapshotUrl = React.useMemo(
        () => resolveDefaultFallbackSnapshotUrl(layoutSnapshotsByKey ?? {}, aiDesignsDataSource),
        [layoutSnapshotsByKey, aiDesignsDataSource],
    );

    React.useEffect(() => {
        if (!open) setSnapshotPreview(null);
    }, [open]);

    return (
        <>
        <Modal
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <SquaresFourIcon size={20} weight="duotone" /> All AI design combinations
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={640}
            className={`editor-ai-tree-cutting-modal obd-theme-${theme}`}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            destroyOnHidden
        >
            <div
                className={`editor-ai-scenarios-block obd-theme-${theme}`}
                style={{ overflow: 'auto', maxHeight: '70vh', padding: '12px 4px' }}
            >
                <p
                    style={{
                        fontSize: 12,
                        color: theme === 'dark' ? 'rgba(203,213,225,0.9)' : '#475569',
                        marginBottom: 14,
                    }}
                >
                    All layout combinations from the current system details. Run AI to populate options.
                </p>
                {(() => {
                    const opts = [
                        { value: 'default', baseLabel: 'oneDRAW Default Maxfill' },
                        ...(roofCombinationsFromAi || []).map((c: any) => ({
                            value: c.name,
                            baseLabel: /^(maximum\s+production|max\s+production(\s+with\s+best\s+roofs)?)$/i.test(
                                (c.name || '').trim(),
                            )
                                ? 'Max Production with Best Roofs'
                                : c.name,
                        })),
                    ];
                    const modulePowerW = (activeModule as any)?.power ?? 0;
                    const defaultRoofIds = (() => {
                        if ((defaultMaxfillRoofIds?.length ?? 0) > 0) {
                            return defaultMaxfillRoofIds;
                        }
                        const allRoofIds = (roofCards || []).map((r: any) => r.roofFaceId).filter(Boolean);
                        return allRoofIds;
                    })();
                    if (opts.length <= 1) {
                        return <p style={{ color: 'rgba(156,163,175,0.9)', fontSize: 13 }}>Run AI to see layout combinations.</p>;
                    }
                    return (
                        <div className="editor-ai-designs-cards">
                            {opts.map((opt: any) => {
                                const key = opt.value;
                                const estProdRaw = layoutProductionByKey?.[key];
                                const estModulesRaw = layoutModulesByKey?.[key];
                                const metrics = (layoutMetricsByKey as any)?.[key] as
                                    | {
                                          efficiency?: number | null;
                                          solarAccess?: number | null;
                                          treeCount?: number | null;
                                          roofCount?: number | null;
                                          multiplier?: number | null;
                                      }
                                    | undefined;
                                const roofCountRaw = resolveLayoutRoofCount({
                                    layoutKey: key,
                                    layoutMetricsByKey: layoutMetricsByKey as Record<
                                        string,
                                        { roofCount?: number | null }
                                    >,
                                    defaultMaxfillRoofIds: defaultRoofIds,
                                    roofCards,
                                    roofCombinationsFromAi,
                                    categoryMainLayoutByCategory,
                                    layoutRoofSummariesByKey,
                                });
                                const cardKeyModal = `${opt.baseLabel}-${key}`;
                                const isSelected = selectedDesignForPricing?.designId === cardKeyModal;
                                const isSimulating = simulatingLayouts && estProdRaw === undefined && estModulesRaw === undefined;
                                const systemKwRaw =
                                    estModulesRaw != null && Number.isFinite(estModulesRaw) && modulePowerW > 0
                                        ? ((estModulesRaw * modulePowerW) / 1000).toFixed(2)
                                        : dashboardSystemSizeKw != null
                                          ? String(dashboardSystemSizeKw)
                                          : '—';
                                const thumbSnapshotResult = catalogCardSnapshotForDisplay(
                                    resolveCatalogCardSnapshot({
                                        layoutKey: key,
                                        categoryLabel: opt.baseLabel,
                                        layoutSnapshotsByKey: layoutSnapshotsByKey ?? {},
                                        categoryMainLayoutByCategory,
                                        aiCategoryLayoutVariants: aiCategoryLayoutVariants ?? {},
                                        aiLayoutVariantSnapshots,
                                        defaultFallbackSnapshotUrl: fallbackSnapshotUrl,
                                        aiDesignsDataSource,
                                        layoutHasMetrics:
                                            (estModulesRaw != null &&
                                                Number.isFinite(estModulesRaw) &&
                                                estModulesRaw > 0) ||
                                            (estProdRaw != null &&
                                                Number.isFinite(estProdRaw) &&
                                                estProdRaw > 0),
                                    }),
                                    isLoadingSnapshots,
                                );
                                const thumbSnapshot = thumbSnapshotResult.url;
                                const thumbSnapshotError = thumbSnapshotResult.error;
                                const effLabelRaw =
                                    metrics?.efficiency != null && Number.isFinite(metrics.efficiency)
                                        ? `${Math.round(metrics.efficiency)} %`
                                        : '—';
                                const solarLabelRaw =
                                    metrics?.solarAccess != null && Number.isFinite(metrics.solarAccess)
                                        ? `${metrics.solarAccess.toFixed(1)}%`
                                        : '—';
                                const treesLabelRaw = resolveSiteTreeCount(treeCount);
                                const multiplierLabelRaw =
                                    metrics?.multiplier != null && Number.isFinite(metrics.multiplier)
                                        ? Number(metrics.multiplier).toFixed(4)
                                        : '—';

                                const estProd = estProdRaw;
                                const estModules = estModulesRaw;
                                const roofCount = roofCountRaw;
                                const systemKw = systemKwRaw;
                                const effLabel = effLabelRaw;
                                const solarLabel = solarLabelRaw;
                                const treesLabel = treesLabelRaw;
                                const multiplierLabel = multiplierLabelRaw;
                                const disqualifiedOverUsage =
                                    !isSimulating && isLayoutProductionOverAnnualUsage(estProd, annualUsageCapKwh);
                                const prodWDisplay =
                                    estModules != null &&
                                    Number.isFinite(estModules as number) &&
                                    modulePowerW > 0
                                        ? `${Math.round((estModules as number) * modulePowerW).toLocaleString()} W`
                                        : '—';
                                const proposalId = getProposalIdForCard(cardKeyModal);
                                return (
                                    <div
                                        key={cardKeyModal}
                                        className={`editor-ai-design-card ${isSelected ? 'editor-ai-design-card--selected' : ''} ${disqualifiedOverUsage ? 'editor-ai-design-card--disqualified' : ''}`}
                                    >
                                        {(isLoadingSnapshots || !!thumbSnapshot || !!thumbSnapshotError) && (
                                            <div
                                                className={`editor-ai-design-card__thumb ${disqualifiedOverUsage ? 'editor-ai-design-card__thumb--not-sellable' : ''}`}
                                            >
                                                {isLoadingSnapshots && !thumbSnapshot ? (
                                                    <Skeleton.Image active className="editor-ai-design-card__skelton" />
                                                ) : thumbSnapshot ? (
                                                    <button
                                                        type="button"
                                                        className="editor-ai-design-card__thumb-zoom"
                                                        onClick={() =>
                                                            setSnapshotPreview({
                                                                url: thumbSnapshot,
                                                                title: opt.baseLabel,
                                                            })
                                                        }
                                                        aria-label={`View full layout image: ${opt.baseLabel}`}
                                                    >
                                                        <img src={thumbSnapshot} alt="Layout snapshot" />
                                                    </button>
                                                ) : thumbSnapshotError ? (
                                                    <div
                                                        className="obd-ai-catalog-list__thumb-error"
                                                        role="status"
                                                        title={thumbSnapshotError}
                                                    >
                                                        <span className="obd-ai-catalog-list__thumb-error-label">
                                                            Snapshot unavailable
                                                        </span>
                                                        <span className="obd-ai-catalog-list__thumb-error-detail">
                                                            {thumbSnapshotError}
                                                        </span>
                                                    </div>
                                                ) : null}
                                                {disqualifiedOverUsage && (
                                                    <div
                                                        className="editor-ai-design-card__not-sellable-ribbon"
                                                        role="img"
                                                        aria-label="Not sellable: estimated production exceeds annual usage"
                                                    >
                                                        <span className="editor-ai-design-card__not-sellable-ribbon__title">
                                                            Not sellable
                                                        </span>
                                                        <span className="editor-ai-design-card__not-sellable-ribbon__sub">
                                                            Annual usage {Math.round(annualUsageCapKwh!).toLocaleString()} kWh/yr —
                                                            production exceeds this
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="editor-ai-design-card__accent" aria-hidden />
                                        <div className="editor-ai-design-card__body">
                                            <div className="editor-ai-design-card__header">
                                                <div className="editor-ai-design-card__title-row">
                                                    <div className="editor-ai-design-card__scenario">
                                                        <span className="editor-ai-tree-cutting-cell__title">
                                                            <SparkleIcon
                                                                size={16}
                                                                weight="duotone"
                                                                className="editor-ai-tree-cutting-cell__icon editor-ai-design-card__scenario-icon"
                                                                aria-hidden
                                                            />
                                                            {opt.baseLabel}
                                                        </span>
                                                    </div>
                                                </div>
                                                {proposalId && (
                                                    <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.85)', marginTop: 2 }}>
                                                        ID: <span style={{ fontFamily: 'monospace' }}>{proposalId}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="editor-ai-design-card__metrics">
                                                <div className="editor-ai-design-card__metric">
                                                    <span className="editor-ai-design-card__metric-label">Modules</span>
                                                    <span className="editor-ai-design-card__metric-value">
                                                        {estModules !== undefined ? estModules : '—'}
                                                    </span>
                                                </div>
                                                <div className="editor-ai-design-card__metric">
                                                    <span className="editor-ai-design-card__metric-label">Roofs</span>
                                                    <span className="editor-ai-design-card__metric-value">{roofCount ?? '—'}</span>
                                                </div>
                                                <div className="editor-ai-design-card__metric">
                                                    <span className="editor-ai-design-card__metric-label">Prod</span>
                                                    <span className="editor-ai-design-card__metric-value">{prodWDisplay}</span>
                                                </div>
                                                <div className="editor-ai-design-card__metric">
                                                    <span className="editor-ai-design-card__metric-label">System</span>
                                                    <span className="editor-ai-design-card__metric-value">{systemKw} kW</span>
                                                </div>
                                            </div>
                                            <div className="editor-ai-design-card__cost">
                                                {isSimulating ? (
                                                    <span className="editor-ai-design-card__est-production editor-ai-design-card__est-production--muted">
                                                        —
                                                    </span>
                                                ) : opt.baseLabel === 'oneDRAW Default Maxfill' ? (
                                                    <span className="editor-ai-scenario-card__base-badge">Default</span>
                                                ) : estProd !== undefined && Number.isFinite(estProd) ? (
                                                    <span className="editor-ai-design-card__est-production">
                                                        {Math.round(estProd).toLocaleString()} kWh/yr{' '}
                                                        <span className="editor-ai-design-card__est-production-hint">est.</span>
                                                    </span>
                                                ) : (
                                                    <span className="editor-ai-design-card__est-production editor-ai-design-card__est-production--muted">
                                                        —
                                                    </span>
                                                )}
                                            </div>
                                            <div className="obd-scenario-card__finance">
                                                <div className="obd-scenario-card__finance-row">
                                                    <span className="obd-scenario-card__finance-label">GOODLEAP (PPA · LOAN · LEASE)</span>
                                                    <span className="obd-scenario-card__finance-value">—</span>
                                                </div>
                                                <div className="obd-scenario-card__finance-row">
                                                    <span className="obd-scenario-card__finance-label">LIGHTREACH TOTALEPCPAYMENT</span>
                                                    <span className="obd-scenario-card__finance-value">—</span>
                                                </div>
                                            </div>
                                            <div className="editor-ai-design-card__content">
                                                {isSimulating ? (
                                                    <div className="editor-ai-design-card__simulating">
                                                        <ArrowsClockwiseIcon size={14} weight="bold" />
                                                        Simulating…
                                                    </div>
                                                ) : (
                                                    <div className="editor-ai-design-card__grid editor-ai-design-card__grid--secondary">
                                                        <div className="editor-ai-design-card__grid-cell">
                                                            <span className="editor-ai-design-card__grid-label editor-ai-design-card__label-with-icon">
                                                                <GaugeIcon
                                                                    size={10}
                                                                    weight="duotone"
                                                                    className="editor-ai-design-card__label-icon"
                                                                    aria-hidden
                                                                />
                                                                Eff.
                                                            </span>
                                                            <span className="editor-ai-design-card__grid-value">{effLabel}</span>
                                                        </div>
                                                        <div className="editor-ai-design-card__grid-cell">
                                                            <span className="editor-ai-design-card__grid-label editor-ai-design-card__label-with-icon">
                                                                <SunIcon
                                                                    size={10}
                                                                    weight="duotone"
                                                                    className="editor-ai-design-card__label-icon"
                                                                    aria-hidden
                                                                />
                                                                Solar access
                                                            </span>
                                                            <span className="editor-ai-design-card__grid-value">{solarLabel}</span>
                                                        </div>
                                                        <div className="editor-ai-design-card__grid-cell">
                                                            <span className="editor-ai-design-card__grid-label editor-ai-design-card__label-with-icon">
                                                                <PercentIcon
                                                                    size={10}
                                                                    weight="duotone"
                                                                    className="editor-ai-design-card__label-icon"
                                                                    aria-hidden
                                                                />
                                                                Multiplier
                                                            </span>
                                                            <span className="editor-ai-design-card__grid-value">{multiplierLabel}</span>
                                                        </div>
                                                        <div className="editor-ai-design-card__grid-cell">
                                                            <span className="editor-ai-design-card__grid-label editor-ai-design-card__label-with-icon">
                                                                <TreeEvergreenIcon
                                                                    size={10}
                                                                    weight="duotone"
                                                                    className="editor-ai-design-card__label-icon"
                                                                    aria-hidden
                                                                />
                                                                Trees
                                                            </span>
                                                            <span className="editor-ai-design-card__grid-value">{treesLabel}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!isSimulating && (
                                            <div className="editor-ai-design-card__footer-btns" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    className="editor-ai-design-card__footer-btn"
                                                    onClick={() => {
                                                        void handleApplyLayoutClick(key);
                                                        onClose();
                                                    }}
                                                    title="Open 3D design"
                                                    aria-label="Open 3D design"
                                                >
                                                    <CubeIcon size={16} weight="duotone" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`editor-ai-design-card__footer-btn ${isSelected ? 'editor-ai-design-card__footer-btn--selected' : ''}`}
                                                    onClick={() => {
                                                        handleSelectLayoutCard(key, {
                                                            estProd,
                                                            estModules,
                                                            systemKw,
                                                            roofCount,
                                                            categoryId: opt.baseLabel,
                                                            designId: cardKeyModal,
                                                        });
                                                        onClose();
                                                    }}
                                                    title={
                                                        disqualifiedOverUsage
                                                            ? 'Not sellable — production exceeds annual usage'
                                                            : 'Select for pricing'
                                                    }
                                                    aria-label="Select for pricing"
                                                    disabled={disqualifiedOverUsage || (pricingSelectionLoading && isSelected)}
                                                >
                                                    {pricingSelectionLoading && isSelected ? (
                                                        <Spin size="small" style={{ display: 'block' }} />
                                                    ) : (
                                                        <CheckSquareIcon size={16} weight={isSelected ? 'fill' : 'duotone'} />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="editor-ai-design-card__footer-btn"
                                                    onClick={() => {
                                                        handlePricingDollarClick(key, {
                                                            estProd,
                                                            estModules,
                                                            systemKw,
                                                            roofCount,
                                                            categoryId: opt.baseLabel,
                                                            designId: cardKeyModal,
                                                        });
                                                        onPricingCallClick?.(key);
                                                        onClose();
                                                    }}
                                                    title={
                                                        disqualifiedOverUsage
                                                            ? 'Production exceeds annual usage'
                                                            : 'Load financing pricing for this design'
                                                    }
                                                    aria-label="Load financing pricing"
                                                    disabled={
                                                        disqualifiedOverUsage ||
                                                        (pricingSelectionLoading &&
                                                            (selectedDesignForPricing?.designId === cardKeyModal ||
                                                                pricingDesignForTrigger?.designId === cardKeyModal))
                                                    }
                                                >
                                                    {pricingSelectionLoading &&
                                                    (selectedDesignForPricing?.designId === cardKeyModal ||
                                                        pricingDesignForTrigger?.designId === cardKeyModal) ? (
                                                        <Spin size="small" style={{ display: 'block' }} />
                                                    ) : (
                                                        <CurrencyDollarIcon size={16} weight="duotone" />
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="editor-ai-design-card__footer-btn"
                                                    onClick={() => {
                                                        setSelectedRoofCombinationKey(key);
                                                        onClose();
                                                        const cached = (treeScenariosByLayoutKey?.[key]?.length ?? 0) > 0;
                                                        const hasData = cached;
                                                        if (hasData) {
                                                            setSelectedRoofCombinationKey(key);
                                                            setTreeCuttingForLayoutKey(key);
                                                            setTreeCuttingPopupOpen(true);
                                                        } else {
                                                            setTreeCuttingConfirmForKey(key);
                                                            setTreeCuttingConfirmOpen(true);
                                                        }
                                                    }}
                                                    title="Tree cutting for this design"
                                                    aria-label="Tree cutting"
                                                >
                                                    <TreeEvergreenIcon size={16} weight="duotone" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="editor-ai-design-card__footer-btn"
                                                    onClick={() => {
                                                        setAltLayoutsCategory(opt.baseLabel);
                                                        setAltLayoutsLoading(false);
                                                        setAltLayoutsModalOpen(true);
                                                    }}
                                                    title="Show layout variations for this category"
                                                    aria-label="Show layout variations"
                                                >
                                                    <SquaresFourIcon size={16} weight="duotone" />
                                                </button>
                                            </div>
                                        )}
                                        {isSelected && <span className="editor-ai-design-card__badge">Selected</span>}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>
        </Modal>
        <Modal
            open={Boolean(snapshotPreview)}
            onCancel={() => setSnapshotPreview(null)}
            footer={null}
            centered
            destroyOnHidden
            width="min(96vw, 1120px)"
            zIndex={10260}
            title={snapshotPreview?.title}
            rootClassName={`obd-ai-snapshot-preview-modal obd-theme-${theme}`}
            classNames={{ body: 'obd-ai-snapshot-preview-modal__body' }}
        >
            {snapshotPreview ? (
                <div className="obd-ai-snapshot-preview-modal__scroll">
                    <img
                        src={snapshotPreview.url}
                        alt={snapshotPreview.title}
                        className="obd-ai-snapshot-preview-modal__img"
                    />
                </div>
            ) : null}
        </Modal>
        </>
    );
};
